<script setup>
// The per-channel PoolShare gain chip — moved verbatim out of PostHeader so the
// «تدبيسة» page intro (and the home shelf strip) show the SAME chip for the
// same channel rather than a look-alike. The rate is owned + computed by the
// backend (`channel.poolShareRate`); this only maps value → visual tier and
// Western-digit text. tier === null ⇒ no active program ⇒ renders nothing.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatPoolShareRate, poolShareRateTier } from '@/utils/poolShareRate.js';

const props = defineProps({
  // A human percent (`1`, `2.5`, `5`); null / 0 / absent → no chip.
  rate: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();

const tier = computed(() => poolShareRateTier(props.rate));
const text = computed(() => formatPoolShareRate(props.rate));
</script>

<template>
  <span
    v-if="tier"
    class="ps-rate"
    :class="`ps-rate--${tier}`"
    :aria-label="t('product.poolShare.ariaLabel', { rate: text })"
  >
    <span class="ps-rate__coin" aria-hidden="true">%</span>
    <span class="ps-rate__pct">{{ text }}</span>
    <span class="ps-rate__label">{{ t('product.poolShare.badgeLabel') }}</span>
  </span>
</template>

<style scoped>
/* ===== PoolShare gain badge — base + tiers (token-driven, no hex here) ===== */
.ps-rate {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: var(--poolit-radius-full);
  font-weight: var(--poolit-font-weight-bold);
  font-size: 11px;
  line-height: 1;
  white-space: nowrap;
  background: var(--ps-rate-bg);
  color: var(--ps-rate-fg);
  border: 1px solid color-mix(in srgb, var(--ps-rate-fg) 16%, transparent);
}

.ps-rate__coin {
  display: grid;
  place-items: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: var(--ps-rate-fg);
  color: var(--ps-rate-coin-ink, #fff);
  font-size: 8px;
  font-weight: 900;
}

/* Western-digit percent — isolate so "5%" never reorders inside the RTL row. */
.ps-rate__pct {
  font-size: 12px;
  direction: ltr;
  unicode-bidi: isolate;
}

.ps-rate__label {
  font-size: 10.5px;
  font-weight: var(--poolit-font-weight-medium);
  opacity: 0.85;
}

.ps-rate--calm {
  --ps-rate-fg: var(--poolshare-rate-calm-fg);
  --ps-rate-bg: var(--poolshare-rate-calm-bg);
}

.ps-rate--green {
  --ps-rate-fg: var(--poolshare-rate-green-fg);
  --ps-rate-bg: var(--poolshare-rate-green-bg);
}

.ps-rate--purple {
  --ps-rate-fg: var(--poolshare-rate-purple-fg);
  --ps-rate-bg: var(--poolshare-rate-purple-bg);
}

/* Gold = top reward: gradient fill + glow, dark ink, gold coin face. */
.ps-rate--gold {
  --ps-rate-fg: var(--poolshare-rate-gold-fg);
  --ps-rate-bg: var(--poolshare-rate-gold-bg);
  --ps-rate-coin-ink: var(--poolshare-rate-gold-accent);
  border-color: transparent;
  box-shadow: var(--poolshare-rate-gold-shadow);
}
</style>
